import React, { useEffect, useState } from 'react';
import { BlogCategory, BlogService } from '../../Services/Blog/BlogService';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import PageTemplate from 'Components/Templates/Page/Page/PageTemplate';
import { ROUTES } from 'Services/Route/Route.types';
import PageHeader from 'Components/Organisms/PageHeader/PageHeader';
import Breadcrumbs from 'Components/Molecules/Breadcrumbs/Breadcrumbs';
import BreadcrumbItem from 'Components/Atoms/BreadcrumbItem/BreadcrumbItem';
import Container from 'Components/Atoms/Container/Container';
import Button from 'Components/Atoms/Button/Button';
import Notification from 'Components/Atoms/Notification/Notification';
import TableFixed from 'Components/Molecules/Table/TableFixed/TableFixed';
import TableHeader from 'Components/Molecules/TableHeader/TableHeader';
import TableColumn from 'Components/Molecules/TableColumn/TableColumn';
import TableBody from 'Components/Molecules/TableBody/TableBody';
import TableRow from 'Components/Molecules/TableRow/TableRow';
import TableCell from 'Components/Molecules/TableCell/TableCell';
import { faTags, faPlus, faEdit, faTrash } from '@fortawesome/pro-solid-svg-icons';
import FontAwesomeIcon from 'Components/Atoms/FontAwesomeIcon/FontAwesomeIcon';
import Text from 'Components/Atoms/Text/Text';
import Badge from 'Components/Atoms/Badge/Badge';
import { TableProvider } from 'Services/Table/TableContext';

const CategoryList: React.FC = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const blogService = BlogService.getInstance();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const categoriesData = await blogService.getCategories();
      setCategories(categoriesData);
    } catch (err) {
      console.error('❌ CategoryList: loadData mislukt', err);
      setError(err instanceof Error ? err.message : 'Categorieën laden mislukt');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Weet je zeker dat je deze categorie wilt verwijderen?')) {
      try {
        await blogService.deleteCategory(id);
        setCategories(categories.filter(category => category.id !== id));
      } catch (err) {
        setError('Categorie verwijderen mislukt.');
      }
    }
  };

  const formatDate = (dateObj?: any): string => {
    if (!dateObj) return 'Geen datum';
    
    // API response bevat date object: {date: "2025-06-19 09:16:51.000000", timezone_type: 3, timezone: "UTC"}
    if (dateObj.date) {
      return new Date(dateObj.date).toLocaleDateString('nl-NL');
    }
    
    // Als string komt, direct gebruiken
    if (typeof dateObj === 'string') {
      return new Date(dateObj).toLocaleDateString('nl-NL');
    }
    
    return 'Geen datum';
  };

  if (loading) return (
    <PageTemplate>
      <Container padding='vertical'>
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <Text>Laden...</Text>
        </div>
      </Container>
    </PageTemplate>
  );

  if (error) return (
    <PageTemplate>
      <Container padding='vertical'>
        <Notification variant='error'>{error}</Notification>
      </Container>
    </PageTemplate>
  );

  return (
    <TableProvider pageNumber={1} totalPageNumber={1} onPageChange={() => {}}>
      <PageTemplate>
        <Helmet>
          <title>{ ROUTES.BLOG.title }</title>
        </Helmet>
        
        <PageHeader
          breadcrumbs={
            <Breadcrumbs>
              <BreadcrumbItem size='small' path={ROUTES.DASHBOARD.path}>Dashboard</BreadcrumbItem>
              <BreadcrumbItem size='small' path={ROUTES.BLOG.path}>Blog</BreadcrumbItem>
              <BreadcrumbItem size='small'>Categorieën</BreadcrumbItem>
            </Breadcrumbs>
          }
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ 
              width: '48px', 
              height: '48px', 
              backgroundColor: '#f3e8ff', 
              borderRadius: '8px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              <FontAwesomeIcon icon={faTags} size="lg" color="#9333ea" />
            </div>
            <div>
              <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827', margin: 0 }}>
                {categories.length} Blog categorieën
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '8px' }}>
                <Badge variant='success'>
                  {categories.length} Totaal
                </Badge>
              </div>
            </div>
          </div>
        </PageHeader>

        <Container padding='vertical'>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginBottom: '24px' }}>
            <Button
              variant='border'
              color='neutral-dark'
              size='medium'
              onClick={() => navigate('/blog')}
              iconBefore={<FontAwesomeIcon icon={faTags} />}
            >
              Blog Artikelen
            </Button>
            <Button
              variant='solid'
              color='primary'
              size='medium'
              onClick={() => navigate('/blog/categories/create')}
              iconBefore={<FontAwesomeIcon icon={faPlus} />}
            >
              Nieuwe Categorie
            </Button>
          </div>
          {categories.length === 0 ? (
            <div style={{ 
              backgroundColor: 'white', 
              borderRadius: '8px', 
              border: '1px solid #e5e7eb', 
              padding: '48px', 
              textAlign: 'center' 
            }}>
              <div style={{ marginBottom: '16px' }}>
                <FontAwesomeIcon icon={faTags} size="2x" color="#9ca3af" />
              </div>
              <Text weight='semibold' color='secondary'>Geen blog categorieën</Text>
              <Text color='secondary'>
                Begin met het maken van je eerste blog categorie.
              </Text>
              <Button
                variant='solid'
                color='primary'
                size='medium'
                onClick={() => navigate('/blog/categories/create')}
                iconBefore={<FontAwesomeIcon icon={faPlus} />}
              >
                Nieuwe Categorie
              </Button>
            </div>
          ) : (
            <div style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              <TableFixed
                header={
                  <TableHeader>
                    <TableColumn>
                      Categorie
                    </TableColumn>
                    <TableColumn>
                      Beschrijving
                    </TableColumn>
                    <TableColumn>
                      Slug
                    </TableColumn>
                    
                    <TableColumn>
                      Acties
                    </TableColumn>
                  </TableHeader>
                }
              >
                <TableBody>
                  {categories.map(category => (
                    <TableRow key={category.id}>
                      <TableCell>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ 
                            width: '40px', 
                            height: '40px', 
                            backgroundColor: '#f3e8ff', 
                            borderRadius: '6px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center' 
                          }}>
                            <FontAwesomeIcon icon={faTags} size="sm" color="#9333ea" />
                          </div>
                          <div>
                            <Text weight='semibold'>{category.name}</Text>
                            <Text color='secondary'>{category.postCount || 0} artikelen</Text>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Text color='secondary'>
                          {category.description || 'Geen beschrijving'}
                        </Text>
                      </TableCell>
                      <TableCell>
                        <Badge variant='info'>
                          {category.slug}
                        </Badge>
                      </TableCell>
                    
                      <TableCell>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <Button
                            variant='transparent'
                            size='small'
                            onClick={() => navigate(`/blog/categories/edit/${category.id}`)}
                            iconBefore={<FontAwesomeIcon icon={faEdit} />}
                          >
                            Bewerken
                          </Button>
                          <Button
                            variant='transparent'
                            size='small'
                            color='error'
                            onClick={() => handleDelete(category.id)}
                            iconBefore={<FontAwesomeIcon icon={faTrash} />}
                          >
                            Verwijderen
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </TableFixed>
            </div>
          )}
        </Container>
      </PageTemplate>
    </TableProvider>
  );
};

export default CategoryList; 